import WhiteboardControlMobile from 'features/toolbar/toolbar/controls/whiteboard/WhiteboardControlMobile';
import React, { ComponentPropsWithoutRef } from 'react';
import { styled } from '@mui/material/styles';
import { ToolbarPlacement } from 'features/toolbar/types';
import { Backdrop, ClickAwayListener, Fade, MenuList } from '@mui/material';
import ChatControlMobile from 'features/toolbar/toolbar/controls/chat/ChatControlMobile';
import LeaveBreakoutControlMobile from 'features/toolbar/toolbar/controls/leave-breakout/LeaveBreakoutControlMobile';
import ParticipantsControlMobile from 'features/toolbar/toolbar/controls/participants/ParticipantsControlMobile';
import RecordingControlMobile from 'features/toolbar/toolbar/controls/recording/RecordingControlMobile';
import SettingsControlMobile from 'features/toolbar/toolbar/controls/settings/SettingsControlMobile';
import RaiseHandControlMobile from 'features/toolbar/toolbar/controls/raise-hand/RaiseHandControlMobile';
import QAControlMobile from 'features/toolbar/toolbar/controls/qa/QAControlMobile';
import PollsControlMobile from 'features/toolbar/toolbar/controls/polls/PollsControlMobile';
import TranscriptionControlMobile from 'features/toolbar/toolbar/controls/transcription/TranscriptionControlMobile';
import ContentLibraryControlMobile from 'features/toolbar/toolbar/controls/content-library/ContentLibraryControlMobile';
import SharedNotesControlMobile from 'features/toolbar/toolbar/shared-notes/SharedNotesControlMobile';
import SIPControlMobile from 'features/toolbar/toolbar/controls/sip/SIPControlMobile';
import RestreamingControlMobile from 'features/toolbar/toolbar/controls/restreaming/RestreamingControlMobile';
import RoomReactionsControlMobile from 'features/toolbar/toolbar/controls/room-reactions/RoomReactionsControlMobile';
import LockControlMobile from 'features/toolbar/toolbar/controls/lock/LockControlMobile';

const Root = styled('div')<{ placement: ToolbarPlacement }>(({ placement, theme }) => {
  const offset = `calc(${theme.toolbar.width} - ${theme.layout.innerPadding})`;

  return {
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: theme.zIndex.appBar,
    padding: theme.layout.innerPadding,
    display: 'flex',
    ...(placement === 'right' && {
      left: 'auto',
      right: offset,
      paddingRight: 0,
      width: '55%',
    }),
    ...(placement === 'left' && {
      left: offset,
      paddingLeft: 0,
      width: '55%',
    }),
    ...(placement === 'bottom' && {
      bottom: offset,
      paddingBottom: 0,
    }),
  };
});

const Inner = styled('div')<{ placement: ToolbarPlacement }>(({ theme, placement }) => ({
  maxHeight: '60vh',
  width: '100%',
  marginTop: 'auto',
  background: theme.toolbar.background,
  overflowY: 'auto',
  '& .MuiMenuItem-root': {
    minHeight: '52px',
    padding: '9px 28px',
    color: theme.palette.getContrastText(theme.toolbar.background),
  },
  '&& .MuiListItemIcon-root': {
    minWidth: '40px',
  },
  '@media (max-height: 500px)': {
    maxHeight: '100%',
  },
  ...(placement === 'left' && {
    borderTopRightRadius: '16px',
    borderBottomRightRadius: '16px',
    '& .MuiList-root': {
      paddingLeft: theme.layout.innerPadding,
    },
  }),
  ...(placement === 'right' && {
    borderTopLeftRadius: '16px',
    borderBottomLeftRadius: '16px',
    '& .MuiList-root': {
      paddingRight: theme.layout.innerPadding,
    },
  }),
  ...(placement === 'bottom' && {
    borderTopLeftRadius: '16px',
    borderTopRightRadius: '16px',
  }),
}));

export type MobileMenuProps = ComponentPropsWithoutRef<'div'> & {
  open: boolean;
  onClose: () => void;
  placement: ToolbarPlacement;
};

const MobileMenu = ({ open, placement, onClose, ...props }: MobileMenuProps) => (
  <>
    <Backdrop
      open={open}
      sx={{
        zIndex: 10,
      }}
      data-testid="mobile-menu-backdrop"
    />
    <Fade in={open} mountOnEnter unmountOnExit>
      <Root {...props} placement={placement}>
        <ClickAwayListener onClickAway={onClose}>
          <Inner placement={placement}>
            <MenuList>
              <ParticipantsControlMobile onClick={onClose} />
              <ChatControlMobile onClick={onClose} />
              <QAControlMobile onClick={onClose} />
              <PollsControlMobile onClick={onClose} />
              <ContentLibraryControlMobile onClick={onClose} />
              <RaiseHandControlMobile onClick={onClose} />
              <RecordingControlMobile onClick={onClose} />
              <RestreamingControlMobile onClick={onClose} />
              <TranscriptionControlMobile onClick={onClose} />
              <WhiteboardControlMobile onClick={onClose} />
              <SharedNotesControlMobile onClick={onClose} />
              <SIPControlMobile onClick={onClose} />
              <RoomReactionsControlMobile onClick={onClose} />
              <LockControlMobile onClick={onClose} />
              <SettingsControlMobile onClick={onClose} />
              <LeaveBreakoutControlMobile />
            </MenuList>
          </Inner>
        </ClickAwayListener>
      </Root>
    </Fade>
  </>
);

export default MobileMenu;
